import { useEffect, useRef } from 'react';
import { EntityName } from '../../../auth/Security';
import type { DeepLinkUIStateOnBeforeInitializationHandler } from '../../types';
import { buildEntityDeepLinkUIStateObject } from '../utils';
import { useDeepLinkContext } from './useDeepLinkContext';
import { useEntityDeepLinkUIStatePath } from './useEntityDeepLinkUIStatePath';
import { extractDeepLinkUIStateInitializationEvent } from './useInitializeDeepLinkUIState.utils';

type UseInitializeListViewDeepLinkUIStateInput<T extends Record<string, unknown>> = {
    entity: EntityName;

    /**
     * Optional deep-link state path override for the main entity node.
     *
     * If not provided, the path is derived from `entity` and parent entity security context.
     */
    entityDeepLinkUIStatePath?: string;

    entityDataList: T[] | undefined;
    selectedEntityDataRecordId: string | undefined;
    getEntityDataRecordId: (item: T) => string | undefined;

    /**
     * Optional callback invoked only after entity data has been validated and converted into a valid deep-link
     * state object, and before the runtime deep-link state slice is written.
     */
    onBeforeInitialize?: DeepLinkUIStateOnBeforeInitializationHandler;

    /**
     * Controls if the initialization value is merged with the existing state value or not.
     * @default true
     */
    merge?: boolean;
};

export const useInitializeListViewDeepLinkUIState = <T extends Record<string, unknown>>({
    entity,
    entityDeepLinkUIStatePath,
    entityDataList,
    selectedEntityDataRecordId,
    getEntityDataRecordId,
    onBeforeInitialize,
    merge = true
}: UseInitializeListViewDeepLinkUIStateInput<T>): void => {
    const isInitializationDone = useRef(false);
    const deepLinkContext = useDeepLinkContext();

    const resolvedEntityDeepLinkUIStatePath = useEntityDeepLinkUIStatePath(entity, entityDeepLinkUIStatePath);

    useEffect(() => {
        const finalizeInitialization = () => {
            isInitializationDone.current = true;

            deepLinkContext.getDeepLinkInitialState({
                path: resolvedEntityDeepLinkUIStatePath,
                clearAfterRead: true
            });
        };

        const setTerminalFallbackState = () => {
            deepLinkContext.setDeepLinkRuntimeState(true, {
                path: resolvedEntityDeepLinkUIStatePath
            });

            finalizeInitialization();
        };

        const initialize = async () => {
            if (isInitializationDone.current) {
                return;
            }

            const maybeInitialState = deepLinkContext.getDeepLinkInitialState<Record<string, unknown> | undefined>({
                path: resolvedEntityDeepLinkUIStatePath
            });

            const initializationEvent = extractDeepLinkUIStateInitializationEvent(
                maybeInitialState as Record<string, unknown> | undefined
            );

            if (!entityDataList) {
                return;
            }

            if (entityDataList.length === 0) {
                setTerminalFallbackState();
                return;
            }

            const deepLinkedEntityId = initializationEvent.entityId;
            const resolvedEntityId = deepLinkedEntityId ?? selectedEntityDataRecordId;

            if (!resolvedEntityId) {
                setTerminalFallbackState();
                return;
            }

            const selectedEntityDataRecord = entityDataList.find(
                (record) => getEntityDataRecordId(record) === resolvedEntityId
            );

            if (!selectedEntityDataRecord) {
                setTerminalFallbackState();
                return;
            }

            const stateObject = buildEntityDeepLinkUIStateObject(entity, selectedEntityDataRecord);

            if (!stateObject) {
                setTerminalFallbackState();
                return;
            }

            try {
                await onBeforeInitialize?.({
                    ...initializationEvent,
                    entityId: resolvedEntityId
                });

                deepLinkContext.setDeepLinkRuntimeState(stateObject, {
                    path: resolvedEntityDeepLinkUIStatePath,
                    merge
                });

                finalizeInitialization();
            } catch (error) {
                console.error(
                    `Failed to initialize entity deep link UI runtime state for path ${resolvedEntityDeepLinkUIStatePath}`,
                    error
                );

                setTerminalFallbackState();
            }
        };

        initialize();
    }, [
        deepLinkContext,
        entity,
        entityDataList,
        getEntityDataRecordId,
        merge,
        onBeforeInitialize,
        resolvedEntityDeepLinkUIStatePath,
        selectedEntityDataRecordId
    ]);

    useEffect(() => {
        if (!isInitializationDone.current || !entityDataList) {
            return;
        }

        const setTerminalFallbackState = () => {
            deepLinkContext.setDeepLinkRuntimeState(true, {
                path: resolvedEntityDeepLinkUIStatePath
            });
        };

        if (!selectedEntityDataRecordId) {
            setTerminalFallbackState();
            return;
        }

        const selectedEntityDataRecord = entityDataList.find(
            (record) => getEntityDataRecordId(record) === selectedEntityDataRecordId
        );

        if (!selectedEntityDataRecord) {
            setTerminalFallbackState();
            return;
        }

        const stateObject = buildEntityDeepLinkUIStateObject(entity, selectedEntityDataRecord);

        if (!stateObject) {
            setTerminalFallbackState();
            return;
        }

        deepLinkContext.setDeepLinkRuntimeState(stateObject, {
            path: resolvedEntityDeepLinkUIStatePath,
            merge: true
        });
    }, [
        deepLinkContext,
        entity,
        entityDataList,
        getEntityDataRecordId,
        resolvedEntityDeepLinkUIStatePath,
        selectedEntityDataRecordId
    ]);
};
